import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader, Loader2, Save } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/utils";
import { Tooltip } from "./tooltip";

const buttonVariants = cva(
  "inline-flex items-center rounded-lg justify-center whitespace-nowrap text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-gray-950 dark:focus-visible:ring-gray-300",
  {
    variants: {
      variant: {
        default: "bg-blue-550 text-gray-50 hover:bg-blue-900",
        destructive: "bg-red-500 text-gray-50 hover:bg-red-500/90",
        outline: "border-2 border-black bg-white text-black hover:bg-gray-100",
        outlineSubtle: "border rounded-lg border-gray-300 bg-white font-bold text-gray-900 hover:bg-gray-100",
        secondary: "bg-gray-100 text-gray-900 hover:bg-gray-100/80 ",
        ghost: "hover:bg-gray-100 hover:text-gray-900 ",
        link: "text-gray-900 underline-offset-4 hover:underline ",
      },
      size: {
        default: "px-4 py-2",
        xs: "px-2 py-1 text-xs",
        sm: "px-3 py-2",
        lg: "px-8 py-4 text-base",
        icon: "p-1 text-xs rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltip?: string;
}

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { endIcon?: React.ReactNode; startIcon?: React.ReactNode; isLoading?: boolean }
>(({ className, variant, size, asChild = false, endIcon, startIcon, isLoading, tooltip, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  const content = asChild ? (
    props.children
  ) : (
    <>
      {startIcon && <span className="mr-2">{startIcon}</span>}
      {props.children}
      {endIcon && <span className="ml-2">{endIcon}</span>}
    </>
  );

  if (isLoading) {
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
      </Comp>
    );
  }

  if (tooltip) {
    return (
      <div>
        <Tooltip title={tooltip}>
          <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            type={props.type || "button"}
            {...props}
          >
            {content}
          </Comp>
        </Tooltip>
      </div>
    );
  }

  return (
    <Comp
      {...(asChild ? {} : { type: "button" })}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    >
      {content}
    </Comp>
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };

export function SaveIcon({ isLoading }: { isLoading: boolean }) {
  return isLoading ? <Loader className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />;
}

export function SaveButton({ isLoading, ...props }: { isLoading: boolean } & React.ComponentProps<"button">) {
  const { t } = useTranslation();

  return (
    <Button variant="default" disabled={isLoading} {...props}>
      <SaveIcon isLoading={isLoading} />
      {t("Speichern")}
    </Button>
  );
}
